import 'bootstrap/dist/css/bootstrap.min.css';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import 'swiper/css';
import 'swiper/css/effect-cards';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import './App.css';
import { getUrlQueryParam } from './common/url';
import { isEmpty, isNil } from 'lodash';
import { Control } from './components/Control';
import { Popup } from './components/Popup';
import { Product } from './components/Product';
import { Sharing } from './components/Sharing';
import { Tool } from './components/Tool';
import { ToolBar } from './components/Toolbar';
import { TabBar } from './components/menu/TabBar';
import { TabBar as TabBarTablet } from './components/menu/tablet/TabBar';
import { useLoadColors } from './hooks/useLoadColors';
import { useLoadLogos } from './hooks/useLoadLogos';
import { useLoadProduct } from './hooks/useLoadProduct';
import { useLoadStyle } from './hooks/useLoadStyle';
import { useLoadPatterns } from './hooks/useLoadPatterns';
import { Hint } from './components/Hint';
import Loading from './components/Loading';
import { Toast } from './components/Toast';
import { TextEditorProvider } from './components/tool/TextEditorContext';
import { useDeviceType } from './hooks/useDeviceType';
import { setRotate, setScale, setShow, setShowText } from './redux-tool/slice/controlSlice';
import { setIsReview, setLoading } from './redux-tool/slice/uiSlice';
import { patternHandling } from './js/patternHandling';

const BASE_URL = process.env.REACT_APP_URL;
const STORE_CODE = process.env.REACT_APP_STORE_CODE;
const TOKEN = getUrlQueryParam('token');
const AUTH = `Bearer ${TOKEN}`;
const queryInfo = { BASE_URL, STORE_CODE, TOKEN, AUTH };

const urlParams = new URLSearchParams(window.location.search);
const customer_id = urlParams.get('customer_id') || '25';
const cart_item_id = urlParams.get('cart_item_id') || '242';
const order_item_id = urlParams.get('order_item_id') || '';
const view_mode = urlParams.get('view_mode') || '';
const productInfo = { customer_id, cart_item_id, order_item_id };

export default function App() {
  const dispatch = useDispatch();
  const { isReview, isShowMenu, isShowMenuToolbar, isShowSharing, isLoading } = useSelector(
    (state) => state.ui,
  );

  //For non-review mode
  const { styleList, defaultStyleLoaded } = useLoadStyle(queryInfo, productInfo);
  const { product } = useLoadProduct(queryInfo, productInfo, defaultStyleLoaded, styleList);
  const { logoList, handleAddNewLogo, handleDeleteLogo } = useLoadLogos(
    queryInfo,
    productInfo,
    product,
  );
  const { colorList } = useLoadColors(queryInfo);
  const { patternList } = useLoadPatterns(queryInfo, productInfo);
  const device = useDeviceType();
  const isMobile = device === 'mobile';

  // common container style
  const containerStyle = {
    height: '100dvh',
    width: '100%',
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    position: isMobile ? 'relative' : undefined,
    justifyContent: isMobile ? (isShowSharing ? 'flex-start' : 'flex-end') : undefined,
  };

  // give each child an order based on device
  const toolStyle = {
    order: isMobile ? 3 : 1,
    display: isShowMenuToolbar ? 'inline-block' : 'none',
    width: isMobile ? '100%' : '20%',
    paddingTop: isMobile ? '10px' : '0px',
  };

  const productStyle = {
    order: 2,
    width: isReview || isShowSharing || isMobile ? '100%' : '60%',
    height: isReview ? '100%' : 'auto',
  };

  const tabBarStyle = {
    order: isMobile ? 2 : 3,
    display: isShowMenu ? 'block' : 'none',
    width: isMobile ? '100%' : '20%',
  };

  useEffect(() => {
    if (isNil(TOKEN) || isEmpty(TOKEN)) return;

    // Handle for case: user want to directly view the "Review" mode
    if (urlParams.has('decal')) {
      dispatch(setIsReview(true));
    }

    //Setup some global functions
    window.showControl = (show, scale, rotate) => {
      dispatch(setShow(show));
      if (scale !== 0) dispatch(setScale(scale));
      if (scale !== 0) dispatch(setRotate(rotate));
    };
    window.showControlText = (show) => {
      dispatch(setShowText(show));
    };
  }, [dispatch]);

  // Preload all pattern textures when patternList is loaded
  useEffect(() => {
    if (patternList && patternList.length > 0) {
      // Show loading while preloading patterns
      dispatch(setLoading(true));

      // Preload all patterns
      patternHandling
        .preloadAllPatterns(patternList, (loaded, total) => {
          // Optional: log progress if needed
          // console.log(`Preloading patterns: ${loaded}/${total}`);
        })
        .then(() => {
          // All patterns preloaded, hide loading
          dispatch(setLoading(false));
        })
        .catch((error) => {
          console.error('Error preloading patterns:', error);
          // Hide loading even on error
          dispatch(setLoading(false));
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patternList]);

  return (
    <>
      {window.isLoading || isLoading ? <Loading /> : null}
      {isNil(TOKEN) || isEmpty(TOKEN) ? (
        <Popup />
      ) : (
        <TextEditorProvider>
          <div
            style={{
              backgroundImage: 'url(/theme/img/background/Background.svg',
              backgroundPosition: 'center',
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              width: '100%',
              position: 'relative',
              height: '100dvh',
              display: 'flex',
              flexDirection: device === 'mobile' ? 'column' : 'row',
              //flexDirection: "column",
              justifyContent: 'end',
            }}
          >
            <div style={containerStyle}>
              {!isReview && (
                <div style={toolStyle}>
                  <Tool
                    logos={logoList}
                    patterns={patternList}
                    colors={colorList}
                    customerId={customer_id}
                    styles={styleList}
                    handleAddNewLogo={handleAddNewLogo}
                    viewMode={view_mode}
                    handleDeleteLogo={handleDeleteLogo}
                  />
                </div>
              )}

              <div style={productStyle}>
                <Product />
              </div>

              {!isReview && (
                <div style={tabBarStyle}>{isMobile ? <TabBar /> : <TabBarTablet />}</div>
              )}
            </div>

            {/*Element that have display: absolute */}
            <ToolBar viewMode={view_mode} />
            <Control />
            <Hint />
            {isReview && (
              <div id="productName-review">
                <span>{product?.Name}</span>
              </div>
            )}
            {/*Elements that have display */}
            {isShowSharing && <Sharing queryInfo={queryInfo} productInfo={productInfo} />}
          </div>
        </TextEditorProvider>
      )}
      <Toast />
    </>
  );
}
